/** @author Rajkumar Rathod */
const PREFIX = '[ehr-widget]';

let debugEnabled = false;

export function setDebug(on: boolean): void {
  debugEnabled = on;
}

export const log = {
  debug(...args: unknown[]): void {
    if (debugEnabled) console.debug(PREFIX, ...args);
  },
  warn(...args: unknown[]): void {
    console.warn(PREFIX, ...args);
  },
  error(...args: unknown[]): void {
    console.error(PREFIX, ...args);
  },
};

export function randomId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  if (c && typeof c.getRandomValues === 'function') {
    const b = c.getRandomValues(new Uint8Array(16));
    return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  }
  // Last resort for very old browsers. Only needs to be unique within one page.
  return 's' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

/** Normalise a host into a bare origin, throwing on anything that is not http(s). */
export function toOrigin(host: string): string {
  const url = new URL(host, window.location.href);
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error(`invalid host protocol: ${url.protocol}`);
  }
  return url.origin;
}

/** Resolve a flow path against the EHR origin, preserving any base path in `host`. */
export function joinUrl(host: string, path: string): URL {
  const base = host.endsWith('/') ? host : host + '/';
  return new URL(path.replace(/^\//, ''), base);
}

export function asArray<T>(value: T | T[] | undefined): T[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

/** Run a callback without letting a host-supplied throw break our internals. */
export function safeCall(fn: () => void, context: string): void {
  try {
    fn();
  } catch (err) {
    log.error(`error in ${context}:`, err);
  }
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
